/**
 * StaffRxFulfilmentPanel — pharmacist prescription fulfilment queue.
 * Shows approved prescriptions progressing through dispensing → ready → collected.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Package, CheckCircle2, Clock, Truck, XCircle } from 'lucide-react';

import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import api from '../lib/api';
import './care.css';

const STATUS_CONFIG = {
  reviewed:   { label: 'Approved',    icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700', next: 'start_dispensing', nextLabel: 'Start dispensing' },
  dispensing: { label: 'Dispensing',   icon: Package,      cls: 'bg-sky-100 text-sky-700',         next: 'ready_for_collection', nextLabel: 'Mark ready' },
  ready:      { label: 'Ready',       icon: Truck,        cls: 'bg-amber-100 text-amber-700',     next: 'collected', nextLabel: 'Mark collected' },
  collected:  { label: 'Collected',   icon: CheckCircle2, cls: 'bg-slate-100 text-slate-600',     next: null, nextLabel: null },
  cancelled:  { label: 'Cancelled',   icon: XCircle,      cls: 'bg-red-100 text-red-700',         next: null, nextLabel: null },
};

const TABS = [
  { key: 'active', label: 'Active' },
  { key: 'reviewed', label: 'Approved' },
  { key: 'dispensing', label: 'Dispensing' },
  { key: 'ready', label: 'Ready' },
  { key: 'collected', label: 'Collected' },
];

export const StaffRxFulfilmentPanel = () => {
  const [tab, setTab] = useState('active');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null); // { rx, action, label }
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get('/staff/portal/prescriptions/fulfilment', { params: { status: tab } })
      .then((r) => {
        setRows(r.data.prescriptions || []);
        setCounts(r.data.counts || {});
      })
      .catch(() => toast.error('Could not load fulfilment queue'))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    if (!acting) return;
    setSaving(true);
    try {
      await api.post(`/staff/portal/prescriptions/${acting.rx.rx_id}/fulfilment`, {
        action: acting.action,
        note: note.trim() || undefined,
      });
      toast.success(`Prescription ${acting.action.replace(/_/g, ' ')}`);
      setActing(null);
      setNote('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="rx-fulfilment-panel">
      <div className="flex items-center gap-2 flex-wrap">
        {TABS.map((t) => {
          const c = t.key === 'active'
            ? (counts.reviewed || 0) + (counts.dispensing || 0) + (counts.ready || 0)
            : counts[t.key] || 0;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`px-3 py-1.5 text-sm font-medium rounded-full transition-colors ${
                tab === t.key ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              data-testid={`fulfilment-tab-${t.key}`}
            >
              {t.label} {c > 0 && <span className="ml-1 text-xs opacity-70">({c})</span>}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm" data-testid="fulfilment-empty">
          <Clock className="h-8 w-8 mx-auto mb-2 opacity-40" />
          No prescriptions in this queue
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((rx) => {
            const cfg = STATUS_CONFIG[rx.status] || STATUS_CONFIG.reviewed;
            const StatusIcon = cfg.icon;
            const patientName = rx.patient_name || 'Unknown patient';
            const meds = rx.ai_scan?.medications?.map((m) => m.medication_name || m.name).filter(Boolean).join(', ');

            return (
              <div key={rx.rx_id} className="border rounded-xl p-4 space-y-2" data-testid={`fulfilment-row-${rx.rx_id}`}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Badge className={cfg.cls + ' text-xs gap-1'}>
                      <StatusIcon className="h-3 w-3" /> {cfg.label}
                    </Badge>
                    <span className="font-medium text-sm truncate">{patientName}</span>
                  </div>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {rx.created_at ? new Date(rx.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''}
                  </span>
                </div>

                {meds && <p className="text-xs text-muted-foreground truncate">Medications: {meds}</p>}

                {cfg.next && (
                  <div className="flex items-center gap-2 pt-1">
                    <Button
                      size="sm"
                      onClick={() => { setActing({ rx, action: cfg.next, label: cfg.nextLabel }); setNote(''); }}
                      data-testid={`fulfilment-action-${rx.rx_id}`}
                    >
                      {cfg.nextLabel}
                    </Button>
                    {rx.status !== 'collected' && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500"
                        onClick={() => { setActing({ rx, action: 'cancelled', label: 'Cancel' }); setNote(''); }}
                        data-testid={`fulfilment-cancel-${rx.rx_id}`}
                      >
                        Cancel
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!acting} onOpenChange={(open) => { if (!open) setActing(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{acting?.label}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              {acting?.action === 'collected'
                ? 'Confirm the patient has collected their medication.'
                : acting?.action === 'cancelled'
                ? 'Provide a reason for cancelling this prescription.'
                : `Move this prescription to "${acting?.label}".`}
            </p>
            <Textarea
              placeholder="Optional note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="fulfilment-note"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActing(null)}>Cancel</Button>
            <Button onClick={doAction} disabled={saving} data-testid="fulfilment-confirm">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
